import React, { FC, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    MessageBarType,
    IDropdownOption,
    PrimaryButton,
    Icon,
    SpinnerSize,
    Spinner,
} from '@fluentui/react';
import {
    Section,
    SectionSize,
    TextField,
    Dropdown,
    Form,
    required,
    ApiException,
    MessageBar,
    LayoutContent,
} from '@eta/components';
import { connect, ConnectedProps } from 'react-redux';
import { completeProfile } from '../Redux/Actions';
import {
    CheckDelegationInvitationValidityQueryResponse,
    CreateUserProfileDto,
    TaxpayerRepresentativeDto,
    LanguageTypes,
    CreateInvitationContactInfoDto,
    ResendInvitationOtpRequest,
    ValidationType,
} from '../../Services/ClientProxy/clientProxy';
import SuccessInvitation from './SuccessInvitation';
import { ReduxState } from '../../common/reducers';

import './CompleteProfile.scss';
import VerifyCompleteProfileOTP from './VerifyCompleteProfileOTP';
import FinalizeCompleteProfile from './FinalizeCompleteProfile';
import { ServiceCreator } from '../../Services/ClientProxy/ServiceFactory';


const mapStateToProps = (state: ReduxState) => ({
    lastError: state.invitation.lastError,
});

const mapDispatchToProps = {
    completeProfile,
};

export interface Props {
    invitationId: string;
    invitationData: CheckDelegationInvitationValidityQueryResponse;
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
const CompleteProfile: FC<PropsFromRedux & Props> = (
    props: PropsFromRedux & Props
) => {
    const emptyCompleteProfile = new CreateUserProfileDto();
    const { invitationData, lastError } = props;
    const [currentProfile, setCurrentProfile] = useState(emptyCompleteProfile);
    const [taxpayerRepresentative, settaxpayerRepresentative] = useState(
        emptyCompleteProfile.taxpayerRepresentative
    );

    const { t } = useTranslation(['resources', 'common', 'VerifyOTP']);
    const [, setIsLoading] = useState<boolean>(true);
    const [showMessageSuccess, setShowMessageSuccess] = useState<boolean>(
        false
    );
    const [error, setError] = useState<string>();
    const [password, setPassword] = useState<string>();
    const [confirmPassword, setConfirmPassword] = useState<string>();

    const [apiErrors, setApiErrors] = useState<ApiException>();
    const [resendOTPInProgress, setResendOTPInProgress] = useState<boolean>(false);
    const [finalizeBtn, setFinalizeBtn] = useState<boolean>(
        false
    );
    const [completed, setCompleted] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const [completeForm, setCompleteForm] = useState<boolean>(true);
    const [otpForm, setOtpForm] = useState<boolean>(false);
    const [finalizeForm, setFinalizeForm] = useState<boolean>(false);
    const [finalizeProfileInProgress, setFinalizeProfileInProgress] = useState<boolean>(false);
    const [otpValue, setotpValue] = useState<string>();
    const [invalidOTP, setInvalidOTP] = useState<boolean>(false);

    const form = useRef(new Form({}));
    const [isFormValid, setIsFormValid] = useState<boolean>(
        form.current.isValid
    );
    const optionsLanguages: IDropdownOption[] = [
        { index: 1, key: LanguageTypes.En, text: t('constants.English') },
        { index: 2, key: LanguageTypes.Ar, text: t('constants.Arabic') },
    ];
    const NotificationChannel: IDropdownOption[] = [
        { key: 1, text: t('Email') },
        { key: 2, text: t('SMS') }
    ];
    form.current.onValidityChanged = (isValid) => setIsFormValid(isValid);

    useEffect(() => {
        const repsentative = new TaxpayerRepresentativeDto();
        repsentative.firstNamePrimaryLang = invitationData.namePrimaryLang;
        repsentative.lastNamePrimaryLang = invitationData.surnamePrimaryLang;
        repsentative.firstNameSecondaryLang = invitationData.nameSecondaryLang;
        repsentative.lastNameSecondaryLang =
            invitationData.surnameSecondaryLang;
        repsentative.email = invitationData.email;
        settaxpayerRepresentative(repsentative);
    }, [invitationData]);

    useEffect(() => {
        if (lastError) {
            setShowMessageSuccess(false);
        }
        setApiErrors(lastError);
        setError(lastError?.serviceError?.toString());
    }, [lastError]);

    const previousForm = () => {
        setOtpForm(false);
        setCompleteForm(true);
        setFinalizeForm(false);
    };

    const saveForm = async () => {
        setIsSaving(true);
        if (form.current.isValid) {
            try {
                setFinalizeBtn(false);
                setIsLoading(true);
                const client = ServiceCreator.getTaxPayerProfileManagementClient(true);
                if (completeForm) {

                    const response = await client.createInvitationContactInfo(
                        props.invitationId,
                        new CreateInvitationContactInfoDto({
                            mobileNo: taxpayerRepresentative?.mobileNo,
                            nationalID: taxpayerRepresentative?.nationalID,
                            preferredLanguage: currentProfile.preferredLanguage
                        })
                    );
                    if (response.isSuccess) {
                        setOtpForm(true);
                        setCompleteForm(false);
                        setotpValue('');
                        setInvalidOTP(false);
                        setError('');
                    }
                } else if (otpValue && otpForm) {
                    const response = await client.validateInvitationOTP(
                        props.invitationId,
                        otpValue
                    );
                    if (response.isSuccess) {
                        setOtpForm(false);
                        setCompleteForm(false);
                        setFinalizeForm(true);
                        setError('');
                    }
                }
                setIsSaving(false);
            } catch (ex) {
                const exception = ex as ApiException;
                if (exception.serviceError?.getFieldError('OtpCode')) {
                    setInvalidOTP(true);
                } else {
                    setError(exception.serviceError?.toString());
                }
                setIsSaving(false);
            }
            setIsLoading(false);
        }
    };

    const finalizeProfile = async () => {
        try {
            setFinalizeProfileInProgress(true);
            currentProfile.taxpayerRepresentative = taxpayerRepresentative;
            currentProfile.invitationID = props.invitationId;
            currentProfile.password = password;
            currentProfile.confirmPassword = confirmPassword;
            const client = ServiceCreator.getTaxPayerProfileManagementClient(true);
            const response = await client.completeUserProfile(
                currentProfile
            );
            if (response) {
                setCompleted(true);
            }
            setFinalizeProfileInProgress(false);
        } catch (ex) {
            const exception = ex as ApiException;
            setError(exception.serviceError?.toString());
            setFinalizeProfileInProgress(false);
        }
    };

    const resendOTP= async() => {
        try {
            setResendOTPInProgress(true);
            const client = ServiceCreator.getTaxPayerProfileManagementClient(true);
            const response = await client.resendInvitationOtp(
                props.invitationId,
                new ResendInvitationOtpRequest({
                    preferredLanguage:currentProfile.preferredLanguage,
                    validationType: ValidationType.Mobile,
                })
            );
            if (response) {
               // setCompleted(true);
            }
            setResendOTPInProgress(false);
        } catch (ex) {
            const exception = ex as ApiException;
            setError(exception.serviceError?.toString());
            setResendOTPInProgress(false);
        }
    };

    return (
        <LayoutContent >
            {!completed && (
                <div className="CompleteProfile">
                    {showMessageSuccess && (
                        <MessageBar
                            messageBarType={MessageBarType.success}
                            isMultiline={false}
                            onDismiss={() => setShowMessageSuccess(false)}
                            className='RelativeMessageBar'
                        >
                            {t('successSaved')}
                        </MessageBar>
                    )}
                    {error && (
                        <MessageBar
                            messageBarType={MessageBarType.error}
                            isMultiline={false}
                            onDismiss={() => setError('')}
                            className='RelativeMessageBar'

                        >
                            {error}
                        </MessageBar>
                    )}
                    {otpForm && (
                        <VerifyCompleteProfileOTP
                            setotpValue={(value: string) => setotpValue(value)}
                            InvalidOTP={invalidOTP}
                            MobileNo={taxpayerRepresentative?.mobileNo}
                            resendOTP={() => resendOTP()}
                            resendOTPInProgress={resendOTPInProgress}
                        />
                    )}
                    {finalizeForm && (
                        <FinalizeCompleteProfile
                            finalizeProfile={
                                (Password, ConfirmPassword, FinalizeBtn) => {
                                    setPassword(Password);
                                    setConfirmPassword(ConfirmPassword);
                                    setFinalizeBtn(FinalizeBtn);
                                }}
                        />
                    )}
                    {completeForm && (
                        <div>
                            <div className="header">
                                <h2>{t('form.components.Profile')}</h2>
                            </div>
                            <div className="bodyLine">
                                <Section
                                    size={SectionSize.h3}
                                    title={t('form.components.Details')}
                                />
                            </div>
                            <div className="bodyLine">
                                <TextField
                                    label={t('UserProfile.FirstNameEN')}
                                    value={taxpayerRepresentative?.firstNamePrimaryLang}
                                    onChange={(_, v) =>
                                        settaxpayerRepresentative(
                                            new TaxpayerRepresentativeDto({
                                                ...taxpayerRepresentative,
                                                firstNamePrimaryLang: v ?? '',
                                            })
                                        )
                                    }
                                    maxLength={50}
                                />
                                <TextField
                                    label={t('UserProfile.SurnameEN')}
                                    value={taxpayerRepresentative?.lastNamePrimaryLang}
                                    onChange={(_, v) =>
                                        settaxpayerRepresentative(
                                            new TaxpayerRepresentativeDto({
                                                ...taxpayerRepresentative,
                                                lastNamePrimaryLang: v ?? '',
                                            })
                                        )
                                    }
                                    maxLength={50}
                                />
                                <TextField
                                    label={t('UserProfile.FirstNameAR')}
                                    value={
                                        taxpayerRepresentative?.firstNameSecondaryLang
                                    }
                                    onChange={(_, v) =>
                                        settaxpayerRepresentative(
                                            new TaxpayerRepresentativeDto({
                                                ...taxpayerRepresentative,
                                                firstNameSecondaryLang: v ?? '',
                                            })
                                        )
                                    }
                                    validationFunc={(v) =>
                                        required(t, v, t('UserProfile.FirstNameAR'))
                                    }
                                    onValidationChange={(isValid) =>
                                        form.current.SetValidity(
                                            'firstNameSecondaryLang',
                                            isValid
                                        )
                                    }
                                    required
                                    maxLength={50}
                                    disabled
                                />
                                <TextField
                                    label={t('UserProfile.SurnameAR')}
                                    value={
                                        taxpayerRepresentative?.lastNameSecondaryLang
                                    }
                                    onChange={(_, v) =>
                                        settaxpayerRepresentative(
                                            new TaxpayerRepresentativeDto({
                                                ...taxpayerRepresentative,
                                                lastNameSecondaryLang: v ?? '',
                                            })
                                        )
                                    }
                                    validationFunc={(v) =>
                                        required(t, v, t('UserProfile.SurnameAR'))
                                    }
                                    onValidationChange={(isValid) =>
                                        form.current.SetValidity(
                                            'lastNameSecondaryLang',
                                            isValid
                                        )
                                    }
                                    required
                                    maxLength={50}
                                    disabled
                                />
                            </div>
                            <div className="bodyLine">
                                <TextField
                                    className='nationalID'
                                    label={t('UserProfile.NationalID')}
                                    value={taxpayerRepresentative?.nationalID}
                                    onChange={(_, v) =>
                                        settaxpayerRepresentative(
                                            new TaxpayerRepresentativeDto({
                                                ...taxpayerRepresentative,
                                                nationalID: v ?? '',
                                            })
                                        )
                                    }
                                    validationFunc={(v) =>
                                        required(t, v, t('UserProfile.NationalID'))
                                    }
                                    onValidationChange={(isValid) =>
                                        form.current.SetValidity('nationalID', isValid)
                                    }
                                    errorMessage={lastError?.serviceError?.getFieldError(
                                        'TaxpayerRepresentative.NationalID'
                                    )}
                                    required
                                    maxLength={14}
                                />
                            </div>

                            <br />
                            <div className="bodyLine">
                                <Section
                                    size={SectionSize.h3}
                                    title={t('form.TaxPayer.Notifications')}
                                />
                            </div>

                            <div className="bodyLine">
                                <TextField
                                    label={t('form.TaxPayer.E-mailAddress')}
                                    value={taxpayerRepresentative?.email}
                                    onChange={(_, v) =>
                                        settaxpayerRepresentative(
                                            new TaxpayerRepresentativeDto({
                                                ...taxpayerRepresentative,
                                                email: v ?? '',
                                            })
                                        )
                                    }
                                    validationFunc={(v) =>
                                        required(
                                            t,
                                            v,
                                            t('form.TaxPayer.E-mailAddress')
                                        )
                                    }
                                    onValidationChange={(isValid) =>
                                        form.current.SetValidity('email', isValid)
                                    }
                                    disabled
                                    required
                                />
                                <TextField
                                    label={t('form.TaxPayer.MobileNumber')}
                                    className='mobilePhone'
                                    value={taxpayerRepresentative?.mobileNo}
                                    toolTip={t('ToolTip.MobileNumber')}
                                    onChange={(_, v) =>
                                        settaxpayerRepresentative(
                                            new TaxpayerRepresentativeDto({
                                                ...taxpayerRepresentative,
                                                mobileNo: v ?? '',
                                            })
                                        )
                                    }
                                    validationFunc={(v) =>
                                        required(
                                            t,
                                            v,
                                            t('form.TaxPayer.MobileNumber')
                                        )
                                    }
                                    errorMessage={apiErrors?.serviceError?.getFieldError(
                                        'TaxpayerRepresentative.MobileNo'
                                    )}
                                    onValidationChange={(isValid) =>
                                        form.current.SetValidity('mobileNo', isValid)
                                    }
                                    required
                                    maxLength={30}
                                />
                                <Dropdown
                                    selectedKey={1}
                                    options={NotificationChannel}
                                    label={t('UserProfile.Notificationpreference')}
                                    disabled
                                    required
                                />
                                <Dropdown
                                    options={optionsLanguages}
                                    label={t('form.TaxPayer.DefaultLanguage')}
                                    onChange={(_, option) =>
                                        setCurrentProfile(
                                            new CreateUserProfileDto({
                                                ...currentProfile,
                                                preferredLanguage:
                                                    option?.key
                                                        ? (option.key as LanguageTypes)
                                                        : LanguageTypes.None,
                                            })
                                        )
                                    }
                                    validationFunc={(v) =>
                                        required(
                                            t,
                                            v,
                                            t('form.TaxPayer.DefaultLanguage')
                                        )
                                    }
                                    onValidationChange={(isValid) =>
                                        form.current.SetValidity('preferredLanguage', isValid)
                                    }
                                    required
                                    selectedKey={currentProfile.preferredLanguage}
                                />
                            </div>
                        </div>
                    )}
                    <div className="btnComplete">
                        {!completeForm && (
                            <PrimaryButton
                                className='previous'
                                text={t('resources:form-components.Previous')}
                                onClick={() => previousForm()}
                                disabled={!isFormValid}
                                iconProps={{ iconName: 'Back' }}
                            />
                        )}
                        {!finalizeForm && (
                            <PrimaryButton
                                className='forward'
                                text={t('resources:form-components.continue')}
                                onClick={() => saveForm()}
                                disabled={isSaving || !((isFormValid && completeForm) || (otpValue && otpForm))}
                                iconProps={{ iconName: 'Forward' }}
                            >
                                {isSaving && <Spinner size={SpinnerSize.small}/>}
                                </PrimaryButton>
                        )}
                        {finalizeForm && (
                            <PrimaryButton
                                text={t('VerifyOTP:finalize_Profile')}
                                onClick={() => finalizeProfile()}
                                disabled={!finalizeBtn || finalizeProfileInProgress}
                                iconProps={{ iconName: 'Save' }}
                                className='btnFinalize'
                            >
                                {finalizeProfileInProgress && <Spinner size={SpinnerSize.small}/>}
                                </PrimaryButton>
                        )
                        }
                    </div>
                    <div className="navigation">
                        <Icon
                            iconName="LocationDot"
                            className={completeForm ? 'Icon d-active' : 'Icon active'}
                        />
                        <Icon
                            iconName="LocationDot"
                            className={otpForm ? 'Icon d-active' : 'Icon active'}
                        />
                        <Icon
                            iconName="LocationDot"
                            className={finalizeForm ? 'Icon d-active' : 'Icon active'}
                        />
                    </div>
                </div>
            )}
            {completed && <SuccessInvitation invitationData={invitationData} />}
        </LayoutContent>
    );
};

export default connector(CompleteProfile);